import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import styles from '../../../assets/styles/LayoutShared.module.css';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface EventoCard {
  uid_evento:          string;
  titulo:              string;
  categoria:           string;
  valor:               number;
  area_texto:          string | null;
  cultura_texto:       string | null;
  veiculo_texto:       string | null;
  data_evento:         string | null;
  origem:              string;
  // ── NOVOS ────────────────────────────────────────────────────────────────
  tipo_operacao_texto: string | null;
  data_programada:     string | null;
  data_inicio:         string | null;
  data_conclusao:      string | null;
  nome_programou:      string | null;
  nome_iniciou:        string | null;
  nome_concluiu:       string | null;
}

type ColunaKey = 'agendado' | 'registrado' | 'em_andamento' | 'concluido';
type Colunas   = Record<ColunaKey, EventoCard[]>;

// ─── Config ───────────────────────────────────────────────────────────────────

const COLUNAS_CONFIG: { key: ColunaKey; label: string; color: string; bg: string }[] = [
  { key: 'registrado',   label: 'Registrado',   color: '#3498db', bg: '#ebf5fb' },
  { key: 'agendado',     label: 'Agendado',     color: '#9b59b6', bg: '#f5f0ff' },
  { key: 'em_andamento', label: 'Em Andamento', color: '#f39c12', bg: '#fef9e7' },
  { key: 'concluido',    label: 'Concluído',    color: '#00b37e', bg: '#f0fff8' },
];

const EMOJI_CATEGORIA: Record<string, string> = {
  operacao_agricola: '🚜', custo: '💸', receita: '💰',
  manutencao: '🔧', abastecimento: '⛽', outros: '📝',
};

const CORES_TIPO_OP: Record<string, string> = {
  'Colheita': '#f39c12', 'Plantio': '#00b37e', 'Pulverização': '#3498db',
  'Adubação': '#9b59b6', 'Irrigação': '#1abc9c', 'Capina/Roçagem': '#e67e22',
  'Poda': '#8e44ad', 'Aplicação': '#2980b9', 'Transporte': '#607d8b', 'Outros': '#aaa',
};

const formatBRL = (v: number) =>
  v > 0 ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : null;

const formatData = (raw: string | null | undefined, incluirAno = false) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  const dia = String(d.getUTCDate()).padStart(2,'0');
  const mes = String(d.getUTCMonth()+1).padStart(2,'0');
  return incluirAno ? `${dia}/${mes}/${d.getUTCFullYear()}` : `${dia}/${mes}`;
};

// ─── CSS Responsivo ───────────────────────────────────────────────────────────

const KANBAN_CSS = `
  .kanban-grid    { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; align-items: start; }
  .kanban-mobile  { display: none; }
  .kanban-desktop { display: block; }
  @media (max-width: 768px) {
    .kanban-grid    { display: none !important; }
    .kanban-mobile  { display: block !important; }
    .kanban-desktop { display: none !important; }
    .kanban-hint    { display: none !important; }
  }
`;

// ─── Componente Principal ─────────────────────────────────────────────────────

const AgroKanban = () => {
  const navigate = useNavigate();
  const [colunas, setColunas] = useState<Colunas>({
    agendado: [], registrado: [], em_andamento: [], concluido: [],
  });
  const [loading, setLoading]   = useState(true);
  const [dragging, setDragging] = useState<{ uid: string; fromColuna: string } | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [abaAtiva, setAbaAtiva] = useState<ColunaKey>('registrado');
  const [movendo, setMovendo]   = useState<string | null>(null);

  const [hasTouch] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  );

  const fetchKanban = useCallback(() => {
    setLoading(true);
    const uid = localStorage.getItem('uid_unidade_producao') || '';
    api.get(`/agro/eventos/kanban${uid ? `?uid_unidade_producao=${uid}` : ''}`)
      .then(res => {
        const data = res.data.data;
        setColunas({
          agendado:     data.agendado     || [],
          registrado:   data.registrado   || [],
          em_andamento: data.em_andamento || [],
          concluido:    data.concluido    || [],
        });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchKanban(); }, [fetchKanban]);

  // ── Monta o payload de ciclo de vida ao mover entre colunas ──────────────────
  // Regras:
  //   → em_andamento : preenche data_inicio + uid_usuario_iniciou (se ainda vazio)
  //   → concluido    : preenche data_conclusao + uid_usuario_concluiu
  //                    e, se data_inicio ainda vazio, preenche também (foi direto)
  //   → agendado / registrado / cancelado : só atualiza status_kanban
  const buildCicloPayload = (card: EventoCard, toColuna: string): Record<string, any> => {
    const agora = new Date().toISOString();
    const uid   = localStorage.getItem('uid_usuario') || '';
    const payload: Record<string, any> = { status_kanban: toColuna };

    if (toColuna === 'em_andamento') {
      // Só preenche se ainda não foi iniciado
      if (!card.data_inicio) {
        payload.data_inicio          = agora;
        if (uid) payload.uid_usuario_iniciou = uid;
      }
    }

    if (toColuna === 'concluido') {
      payload.data_conclusao          = agora;
      if (uid) payload.uid_usuario_concluiu = uid;
      // Se nunca foi iniciado, marca início também (fluxo pulado)
      if (!card.data_inicio) {
        payload.data_inicio          = agora;
        if (uid) payload.uid_usuario_iniciou = uid;
      }
    }

    return payload;
  };

  const handleDrop = async (toColuna: string) => {
    if (!dragging || dragging.fromColuna === toColuna) {
      setDragging(null); setDragOver(null); return;
    }
    const { uid, fromColuna } = dragging;
    const card = colunas[fromColuna as ColunaKey].find(c => c.uid_evento === uid);
    if (!card) return;
    const payload = buildCicloPayload(card, toColuna);
    // Atualiza card localmente com os novos campos para refletir imediatamente
    const cardAtualizado = {
      ...card,
      data_inicio:    payload.data_inicio    ?? card.data_inicio,
      data_conclusao: payload.data_conclusao ?? card.data_conclusao,
    };
    setColunas(prev => {
      const n = { ...prev };
      n[fromColuna as ColunaKey] = n[fromColuna as ColunaKey].filter(c => c.uid_evento !== uid);
      n[toColuna as ColunaKey]   = [cardAtualizado, ...n[toColuna as ColunaKey]];
      return n;
    });
    setDragging(null); setDragOver(null);
    try { await api.patch(`/agro/eventos/${uid}`, payload); }
    catch { fetchKanban(); }
  };

  const handleMoverCard = async (uid: string, fromColuna: ColunaKey, toColuna: ColunaKey) => {
    if (fromColuna === toColuna) return;
    const card = colunas[fromColuna].find(c => c.uid_evento === uid);
    if (!card) return;
    const payload = buildCicloPayload(card, toColuna);
    const cardAtualizado = {
      ...card,
      data_inicio:    payload.data_inicio    ?? card.data_inicio,
      data_conclusao: payload.data_conclusao ?? card.data_conclusao,
    };
    setMovendo(uid);
    setColunas(prev => {
      const n = { ...prev };
      n[fromColuna] = n[fromColuna].filter(c => c.uid_evento !== uid);
      n[toColuna]   = [cardAtualizado, ...n[toColuna]];
      return n;
    });
    try { await api.patch(`/agro/eventos/${uid}`, payload); }
    catch { fetchKanban(); }
    finally { setMovendo(null); }
  };

  const totalEventos = Object.values(colunas).reduce((acc, col) => acc + col.length, 0);
  const colAtiva     = COLUNAS_CONFIG.find(c => c.key === abaAtiva)!;

  return (
    <div className={styles.container}>
      <style>{KANBAN_CSS}</style>

      <div className={styles.headerTitle} style={{ marginBottom: 16 }}>
        <h1 className={styles.greenTitle}>Kanban</h1>
        <p className="kanban-hint" style={{ color: '#666', fontSize: '0.85rem', alignSelf: 'center' }}>
          {totalEventos} eventos · arraste para mover · clique para ver detalhes
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Carregando...</div>
      ) : (
        <>
          {/* ── DESKTOP ─────────────────────────────────────────────────── */}
          <div className="kanban-desktop">
            <div className="kanban-grid">
              {COLUNAS_CONFIG.map(col => (
                <div
                  key={col.key}
                  onDragOver={e => { e.preventDefault(); setDragOver(col.key); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={e => { e.preventDefault(); handleDrop(col.key); }}
                  style={{
                    background: dragOver === col.key ? col.bg : '#f8f9fa',
                    borderRadius: 12,
                    border: dragOver === col.key ? `2px dashed ${col.color}` : '2px solid transparent',
                    transition: 'all 0.2s',
                    minHeight: 200,
                    padding: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: col.color }} />
                      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#333' }}>{col.label}</span>
                    </div>
                    <span style={{ background: col.color, color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: '0.75rem', fontWeight: 700 }}>
                      {colunas[col.key].length}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {colunas[col.key].length === 0 && (
                      <div style={{ textAlign: 'center', padding: '24px 8px', color: '#bbb', fontSize: '0.8rem', border: '2px dashed #e0e0e0', borderRadius: 8 }}>
                        Solte aqui
                      </div>
                    )}
                    {colunas[col.key].map(card => (
                      <CardDesktop
                        key={card.uid_evento}
                        card={card}
                        colunaKey={col.key}
                        hasTouch={hasTouch}
                        onDragStart={(uid, col) => setDragging({ uid, fromColuna: col })}
                        onMove={(uid, to) => handleMoverCard(uid, col.key as ColunaKey, to)}
                        movendo={movendo === card.uid_evento}
                        onClick={uid => navigate(`/agro/eventos/${uid}`)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── MOBILE ──────────────────────────────────────────────────── */}
          <div className="kanban-mobile">
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#f0f0f0', borderRadius: 12, padding: 4 }}>
              {COLUNAS_CONFIG.map(col => {
                const isActive = abaAtiva === col.key;
                return (
                  <button key={col.key} onClick={() => setAbaAtiva(col.key)} style={{
                    flex: 1, padding: '8px 4px', border: 'none', borderRadius: 9, cursor: 'pointer',
                    fontWeight: isActive ? 700 : 500, fontSize: '0.72rem',
                    background: isActive ? '#fff' : 'transparent',
                    color: isActive ? col.color : '#999',
                    boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.18s ease',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: isActive ? col.color : '#ccc', display: 'block' }} />
                    {col.label}
                    <span style={{ background: isActive ? col.color : '#ddd', color: isActive ? '#fff' : '#888', borderRadius: 10, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700 }}>
                      {colunas[col.key].length}
                    </span>
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '10px 14px', borderRadius: 10, background: colAtiva.bg }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: colAtiva.color, flexShrink: 0 }} />
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: colAtiva.color }}>{colAtiva.label}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: colAtiva.color, fontWeight: 600 }}>
                {colunas[abaAtiva].length} {colunas[abaAtiva].length === 1 ? 'evento' : 'eventos'}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {colunas[abaAtiva].length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#bbb', fontSize: '0.85rem', border: '2px dashed #e8e8e8', borderRadius: 12, background: '#fafafa' }}>
                  Nenhum evento aqui
                </div>
              )}
              {colunas[abaAtiva].map(card => (
                <CardMobile
                  key={card.uid_evento}
                  card={card}
                  colunaAtual={abaAtiva}
                  movendo={movendo === card.uid_evento}
                  onMove={handleMoverCard}
                  onClick={uid => navigate(`/agro/eventos/${uid}`)}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Linha de ciclo de vida (data + responsável) ──────────────────────────────

const LinhaCiclo: React.FC<{ icon: string; label: string; data: string | null; nome: string | null; color: string }> = ({ icon, label, data, nome, color }) => {
  if (!data && !nome) return null;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:3 }}>
      <span style={{ fontSize:'0.7rem' }}>{icon}</span>
      <span style={{ fontSize:'0.7rem', color, fontWeight:600 }}>{label}:</span>
      {data && <span style={{ fontSize:'0.7rem', color:'#666' }}>{formatData(data)}</span>}
      {nome && <span style={{ fontSize:'0.7rem', color:'#888' }}>· {nome}</span>}
    </div>
  );
};

// ─── Card Desktop ─────────────────────────────────────────────────────────────

const CardDesktop: React.FC<{
  card: EventoCard;
  colunaKey: string;
  hasTouch: boolean;
  movendo: boolean;
  onDragStart: (uid: string, coluna: string) => void;
  onMove: (uid: string, to: ColunaKey) => void;
  onClick: (uid: string) => void;
}> = ({ card, colunaKey, hasTouch, movendo, onDragStart, onMove, onClick }) => {
  const valor = formatBRL(card.valor);
  const [isDragging, setIsDragging] = useState(false);
  const [showMover, setShowMover]   = useState(false);

  const DESTINOS = COLUNAS_CONFIG.filter(c => c.key !== colunaKey);
  const tipoColor = card.tipo_operacao_texto ? (CORES_TIPO_OP[card.tipo_operacao_texto] || '#607d8b') : null;

  return (
    <div style={{
      background: '#fff', borderRadius: 10,
      boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
      border: '1px solid #f0f0f0', userSelect: 'none',
      transition: 'box-shadow 0.15s, transform 0.15s',
      opacity: isDragging || movendo ? 0.5 : 1,
      overflow: 'hidden',
    }}
      onMouseEnter={e => {
        if (hasTouch) return;
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.13)';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        if (hasTouch) return;
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)';
        (e.currentTarget as HTMLDivElement).style.transform = '';
      }}
    >
      <div
        draggable={!hasTouch}
        onDragStart={() => { setIsDragging(true); onDragStart(card.uid_evento, colunaKey); }}
        onDragEnd={() => setIsDragging(false)}
        onClick={() => !isDragging && !showMover && onClick(card.uid_evento)}
        style={{ padding: '12px 14px', cursor: hasTouch ? 'pointer' : 'grab' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: '1.1rem' }}>{EMOJI_CATEGORIA[card.categoria] || '📝'}</span>
          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: '#333', lineHeight: 1.3, flex: 1 }}>
            {card.titulo}
          </p>
        </div>

        {/* Tipo de operação */}
        {card.tipo_operacao_texto && (
          <div style={{ marginBottom:5 }}>
            <span style={{ background:`${tipoColor}18`, color: tipoColor!, padding:'2px 8px', borderRadius:8, fontSize:'0.7rem', fontWeight:700 }}>
              {card.tipo_operacao_texto}
            </span>
          </div>
        )}

        {valor && (
          <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '0.9rem', color: card.categoria === 'receita' ? '#00b37e' : '#e74c3c' }}>
            {valor}
          </p>
        )}

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {card.area_texto    && <Tag>📍 {card.area_texto}</Tag>}
          {card.cultura_texto && <Tag>🌱 {card.cultura_texto}</Tag>}
          {card.veiculo_texto && <Tag>🚜 {card.veiculo_texto}</Tag>}
          {card.data_evento   && <Tag style={{ marginLeft: 'auto', color: '#aaa' }}>{formatData(card.data_evento)}</Tag>}
        </div>

        {/* Ciclo de vida */}
        <div style={{ marginTop:4 }}>
          <LinhaCiclo icon="📅" label="Programado" data={card.data_programada} nome={card.nome_programou} color="#9b59b6" />
          <LinhaCiclo icon="▶️" label="Iniciado"   data={card.data_inicio}     nome={card.nome_iniciou}   color="#f39c12" />
          <LinhaCiclo icon="✅" label="Concluído"  data={card.data_conclusao}  nome={card.nome_concluiu}  color="#00b37e" />
        </div>

        {card.origem === 'whatsapp' && (
          <div style={{ marginTop: 6 }}>
            <Tag style={{ background: '#e8f8f0', color: '#00b37e' }}>📱 WhatsApp</Tag>
          </div>
        )}
      </div>

      {hasTouch && (
        <div style={{ borderTop: '1px solid #f5f5f5', padding: '7px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa', gap: 6 }}>
          <button onClick={() => setShowMover(v => !v)} style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 6, padding: '4px 10px', fontSize: '0.7rem', fontWeight: 600, color: '#666', cursor: 'pointer' }}>
            {showMover ? '✕' : '↔ Mover'}
          </button>
          {showMover && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {DESTINOS.map(dest => (
                <button key={dest.key} onClick={() => { onMove(card.uid_evento, dest.key); setShowMover(false); }} disabled={movendo}
                  style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: dest.color, color: '#fff', fontSize: '0.67rem', fontWeight: 700, cursor: 'pointer' }}>
                  {dest.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Card Mobile ──────────────────────────────────────────────────────────────

const CardMobile: React.FC<{
  card: EventoCard;
  colunaAtual: ColunaKey;
  movendo: boolean;
  onMove: (uid: string, from: ColunaKey, to: ColunaKey) => void;
  onClick: (uid: string) => void;
}> = ({ card, colunaAtual, movendo, onMove, onClick }) => {
  const valor = formatBRL(card.valor);
  const [showMover, setShowMover] = useState(false);
  const DESTINOS = COLUNAS_CONFIG.filter(c => c.key !== colunaAtual);
  const tipoColor = card.tipo_operacao_texto ? (CORES_TIPO_OP[card.tipo_operacao_texto] || '#607d8b') : null;

  return (
    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0', opacity: movendo ? 0.6 : 1 }}>
      <div onClick={() => !showMover && onClick(card.uid_evento)} style={{ padding: '14px 16px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{EMOJI_CATEGORIA[card.categoria] || '📝'}</span>
          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.88rem', color: '#222', lineHeight: 1.35, flex: 1 }}>
            {card.titulo}
          </p>
        </div>

        {card.tipo_operacao_texto && (
          <div style={{ marginBottom:6 }}>
            <span style={{ background:`${tipoColor}18`, color: tipoColor!, padding:'2px 8px', borderRadius:8, fontSize:'0.72rem', fontWeight:700 }}>
              {card.tipo_operacao_texto}
            </span>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {card.area_texto    && <Tag>📍 {card.area_texto}</Tag>}
            {card.cultura_texto && <Tag>🌱 {card.cultura_texto}</Tag>}
            {card.veiculo_texto && <Tag>🚜 {card.veiculo_texto}</Tag>}
            {card.origem === 'whatsapp' && <Tag style={{ background: '#e8f8f0', color: '#00b37e' }}>📱</Tag>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {valor && <span style={{ fontWeight: 700, fontSize: '0.9rem', color: card.categoria === 'receita' ? '#00b37e' : '#e74c3c' }}>{valor}</span>}
            {card.data_evento && <span style={{ fontSize: '0.75rem', color: '#aaa' }}>{formatData(card.data_evento)}</span>}
          </div>
        </div>

        {/* Ciclo de vida compacto */}
        <div style={{ marginTop:6 }}>
          <LinhaCiclo icon="📅" label="Programado" data={card.data_programada} nome={card.nome_programou} color="#9b59b6" />
          <LinhaCiclo icon="▶️" label="Iniciado"   data={card.data_inicio}     nome={card.nome_iniciou}   color="#f39c12" />
          <LinhaCiclo icon="✅" label="Concluído"  data={card.data_conclusao}  nome={card.nome_concluiu}  color="#00b37e" />
        </div>
      </div>

      <div style={{ borderTop: '1px solid #f5f5f5', padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa' }}>
        <button onClick={() => setShowMover(v => !v)} style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 6, padding: '4px 12px', fontSize: '0.72rem', fontWeight: 600, color: '#666', cursor: 'pointer' }}>
          {showMover ? '✕ Fechar' : '↔ Mover'}
        </button>
        {showMover && (
          <div style={{ display: 'flex', gap: 6 }}>
            {DESTINOS.map(dest => (
              <button key={dest.key} onClick={() => { onMove(card.uid_evento, colunaAtual, dest.key); setShowMover(false); }} disabled={movendo}
                style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: dest.color, color: '#fff', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer' }}>
                {dest.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Tag ──────────────────────────────────────────────────────────────────────

const Tag: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <span style={{ background: '#f5f5f5', color: '#666', padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 500, ...style }}>
    {children}
  </span>
);

export default AgroKanban;